"use client";

import React from "react";
import { Calendar, User, Clock, ArrowLeft, Share2, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="space-y-8 animate-pulse">
          <div className="h-8 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="aspect-video bg-muted rounded-xl w-full" />
          <div className="space-y-4">
            <div className="h-4 bg-muted rounded w-full" />
            <div className="h-4 bg-muted rounded w-full" />
            <div className="h-4 bg-muted rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <div className="bg-destructive/10 text-destructive p-6 rounded-lg inline-block">
          <h3 className="text-lg font-bold mb-2">Erro ao carregar o post</h3>
          <p>{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h3 className="text-xl font-bold text-muted-foreground">Post não encontrado.</h3>
        <div className="mt-6">
          <Link to="/">
            <Button>Voltar para a Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto px-4 py-12 max-w-4xl">
      {}
      <div className="mb-8">
        <Link to="/blog">
          <Button variant="ghost" className="pl-0 hover:pl-2 transition-all text-muted-foreground">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para o Blog
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-10 text-center md:text-left">
        <div className="flex flex-wrap gap-2 mb-6 justify-center md:justify-start">
          {post.categories?.map((cat) => (
            <span
              key={cat.id}
              className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
            >
              {cat.name}
            </span>
          ))}
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6 leading-tight">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-muted overflow-hidden flex items-center justify-center border border-border">
                {post.author.avatar ? (
                  <img 
                    src={Object.values(post.author.avatar)[0]} 
                    alt={post.author.name} 
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <User className="h-4 w-4" />
                )}
              </div>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(post.date)}</span>
          </div>

          {}
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>5 min de leitura</span>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 rounded-2xl overflow-hidden shadow-lg border border-border bg-muted">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover max-h-[600px]"
          />
        </div>
      )}

      {}
      <div className="prose prose-lg prose-neutral dark:prose-invert max-w-none mb-12">
        {}
        <div dangerouslySetInnerHTML={{ __html: post.content || "" }} />
      </div>

      <Separator className="my-12" />

      {}
      <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12">
        <div className="flex flex-wrap gap-2">
          {post.tags && post.tags.length > 0 && (
            <>
              <Tag className="h-4 w-4 text-muted-foreground mt-1" />
              {post.tags.map((tag) => (
                <span key={tag.id} className="text-sm text-muted-foreground hover:text-primary cursor-pointer transition-colors">
                  #{tag.name}
                </span>
              ))}
            </>
          )}
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-muted-foreground">Compartilhar:</span>
          <Button variant="outline" size="icon" className="rounded-full">
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {}
      {post.author && (
        <Card className="bg-muted/30 border-border/60">
          <CardContent className="flex flex-col md:flex-row items-center md:items-start gap-6 p-8">
            <div className="h-20 w-20 rounded-full bg-background border-2 border-border overflow-hidden shrink-0">
               {post.author.avatar ? (
                  <img 
                    src={Object.values(post.author.avatar)[0]} 
                    alt={post.author.name} 
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center bg-muted">
                    <User className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
            </div>
            <div className="text-center md:text-left space-y-2">
              <h3 className="text-lg font-bold text-foreground">Sobre {post.author.name}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Especialista em engenharia térmica e soluções para ambientes críticos. 
                Contribui regularmente com artigos técnicos sobre refrigeração industrial e controle de precisão.
              </p>
              <Link to={post.author.link || "#"}>
                <Button variant="link" className="px-0 h-auto text-primary">
                  Ver todos os posts do autor
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  
  const current_wp_query_params = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", p: 1 }; 

  
  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}